"use client";

import { OTPFieldPreview as OTPField } from "@base-ui/react/otp-field";
import { FormControl, type FormInputProps, useControl } from "@offstreetparking/design-system/forms";
import { classNames } from "@offstreetparking/design-system/utils";

const SLOT_COUNT = 6;

const rootClasses = "my-2 flex w-full items-center justify-center gap-2 sm:gap-3";

const slotClasses = classNames(
	"h-12 w-11 min-w-0 px-0 rounded-md border border-neutral bg-white text-center font-medium text-lg text-neutral outline-none transition-colors",
	"focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary",
	"data-[filled]:border-primary",
	"disabled:bg-neutral-faded disabled:opacity-70",
	"aria-invalid:border-critical aria-invalid:ring-critical",
);

export type OTPInputProps = Pick<FormInputProps<string>, "name" | "label" | "isRequired"> & {
	autoFocus?: boolean;
	isLabelVisible?: boolean;
	onComplete?: (value: string) => void;
	onValueChange?: (value: string) => void;
};

export const OTPInput = ({
	name,
	label,
	isRequired,
	isLabelVisible,
	autoFocus,
	onComplete,
	onValueChange,
}: OTPInputProps) => {
	const { error, field } = useControl({ name });

	return (
		<FormControl
			name={name}
			label={label}
			error={error}
			isRequired={isRequired}
			labelClassName={isLabelVisible ? "text-center" : "sr-only"}
			errorClassName="justify-center"
		>
			<OTPField.Root
				ref={field.ref}
				id={name}
				name={name}
				value={field.value ?? ""}
				onValueChange={(value) => {
					field.onChange(value);
					onValueChange?.(value);
				}}
				onValueComplete={(value) => onComplete?.(value)}
				onBlur={(event) => {
					const { relatedTarget } = event;
					if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
						return;
					}
					if (!field.value) {
						return;
					}
					field.onBlur();
				}}
				length={SLOT_COUNT}
				validationType="numeric"
				autoComplete="one-time-code"
				required={isRequired}
				aria-invalid={error ? true : undefined}
				className={rootClasses}
			>
				{Array.from({ length: SLOT_COUNT }, (_, index) => (
					<OTPField.Input
						// biome-ignore lint/suspicious/noArrayIndexKey: slot order is fixed and stable
						key={index}
						autoFocus={autoFocus && index === 0}
						aria-invalid={error ? true : undefined}
						className={slotClasses}
					/>
				))}
			</OTPField.Root>
		</FormControl>
	);
};
